<template>
  <div class="workflow" :key="key">
    <div class="workflow__dropzone">
      <div class="d-flex align-items-center workflow__heading">
        <h2 class="textStyle mb-3">{{ $t("training.program.add_files") }}</h2>
      </div>
      <FileDropzoneTypeComponent
        :existingFiles="training.programFiles"
        ref="dropzone"
        :confName="confName"
        :confPath="confPath"
        :confSubject="confSubject"
        :maxFiles="10"
        :extensionsAccepted="/^([./])(pdf|doc|docx|xls|xlsx|msword)$/"
        :extensionsAcceptedMessage="$t('training.program.extension_accepted_message')"
        @uploading="uploading"
        @uploaded="uploaded"
        @askRemove="remove"
      />
    </div>
    <div class="workflow__actions">
      <div class="d-flex align-items-center workflow__heading">
        <h2 class="textStyle">{{ $t("training.program.training_program") }}</h2>
      </div>

      <div class="row align-items-center my-4">
        <template v-for="(step, index) in steps" :key="step.id">
          <div class="col-xl-6" :class="{ 'mt-2 mt-xl-0': index > programConstante.PROGRAM_STATUS_DISABLE }">
            <div
              class="workflow__step"
              :class="{ '-success': step.status === programConstante.PROGRAM_STATUS_SUCCESS }"
            >
              <div class="workflow__step__number">{{ index }}</div>
              <div class="workflow__step__content">
                <div class="workflow__step__content__label" v-if="step.label">{{ step.label }}</div>
                <div class="workflow__step__content__status" v-if="step.statusLabel">{{ step.statusLabel }}</div>
                <div class="workflow__step__content__date" v-if="step.date">{{ step.date }}</div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="row">
        <template v-for="(step, index) in steps" :key="index">
          <div class="col-xl-6" :class="{ 'mt-2 mt-xl-0': index > programConstante.PROGRAM_STATUS_DISABLE }">
            <SubmitWithLoaderComponent
              :text="step.buttonText"
              :loading="step.loading"
              :disabled="step.status < programConstante.PROGRAM_STATUS_ENABLE"
              :cfaStyle="true"
              :fullSize="true"
              color="success"
              itemClass="w-100"
              @submit="action(step.action)"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { dateFormatter } from "@/services/utilities/date-formatter";
import SubmitWithLoaderComponent from "@/components/utilities/SubmitWithLoaderComponent.vue";
import { programConstante } from "@/constantes/program";
import FileDropzoneTypeComponent from "@/components/form/FileDropzoneTypeComponent.vue";
import { trainingManager } from "@/services/training/training-manager";
import { useUserStore } from "@/store/user/user";
import { mapState } from "pinia";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";

export default {
  name: "TrainingProgramWorkflowComponent",
  components: { FileDropzoneTypeComponent, SubmitWithLoaderComponent },
  emits: ["import", "publish"],
  props: {
    training: {
      type: Object,
      required: true,
    },
    importing: {
      type: Boolean,
      required: true,
    },
    publishing: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    programConstante() {
      return programConstante;
    },
    hasBeenImported() {
      return !!this.training.lastProgramImportDate;
    },
    hasBeenPublished() {
      return !!this.training.lastProgramPublishDate;
    },

    ...mapState(useUserStore, {
      user: (store) => store.user,
    }),
  },
  data() {
    return {
      key: 0,
      isUploadingFiles: false,
      confName: "training",
      confPath: "program",
      confSubject: null,
      steps: {
        1: {
          id: 1,
          label: i18n.global.t("training.program.steps.import.title"),
          status: programConstante.PROGRAM_STATUS_ENABLE,
          statusLabel: i18n.global.t("training.program.steps.incoming"),
          date: "",
          buttonText: i18n.global.t("training.program.steps.import.button"),
          action: "import",
          loading: false,
        },
        2: {
          id: 1,
          label: i18n.global.t("training.program.steps.publish.title"),
          status: programConstante.PROGRAM_STATUS_ENABLE,
          statusLabel: i18n.global.t("training.program.steps.incoming"),
          date: "",
          buttonText: i18n.global.t("training.program.steps.publish.button"),
          action: "publish",
          loading: false,
        },
      },
    };
  },
  watch: {
    training() {
      this.init();
    },

    importing() {
      this.steps[1].loading = this.importing;
      this.steps[1].statusLabel = i18n.global.t("training.program.steps.inprogress");
      this.steps[1].date = null;
    },

    publishing() {
      this.steps[2].loading = this.publishing;
      this.steps[2].statusLabel = i18n.global.t("training.program.steps.inprogress");
      this.steps[2].date = null;
    },

    isUploadingFiles() {
      this.steps[1].status = this.isUploadingFiles
        ? programConstante.PROGRAM_STATUS_DISABLE
        : this.hasBeenImported
          ? programConstante.PROGRAM_STATUS_SUCCESS
          : programConstante.PROGRAM_STATUS_ENABLE;
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.setProgression();
      this.confSubject = this.training.cryptedId;
    },

    setProgression() {
      if (this.hasBeenImported) {
        this.hasBeenImportedProgress();
      }
      if (this.hasBeenPublished) {
        this.hasBeenPublishedProgress();
      }
      this.key++;
    },

    hasBeenImportedProgress() {
      this.steps[1].status = programConstante.PROGRAM_STATUS_SUCCESS;
      this.steps[1].statusLabel = this.importing
        ? i18n.global.t("training.program.steps.inprogress")
        : i18n.global.t("training.program.steps.finished");
      this.steps[1].date = dateFormatter.format(this.training.lastProgramImportDate, "short");
      this.steps[1].loading = this.importing;
    },

    hasBeenPublishedProgress() {
      this.steps[2].status = programConstante.PROGRAM_STATUS_SUCCESS;
      this.steps[2].statusLabel = this.publishing
        ? i18n.global.t("training.program.steps.inprogress")
        : i18n.global.t("training.program.steps.finished");
      this.steps[2].date = dateFormatter.format(this.training.lastProgramPublishDate, "short");
      this.steps[2].loading = this.publishing;
    },

    action(action) {
      switch (action) {
        case "import":
          this.import();
          break;
        case "publish":
          this.publish();
          break;
      }
    },

    import() {
      this.$emit("import");
    },

    publish() {
      this.$emit("publish");
    },

    uploading() {
      this.isUploadingFiles = true;
    },

    async uploaded(payload) {
      const fileIds = payload.files.map((file) => file.id);
      const res = await trainingManager.post(this.$route.params.trainingId, "program/add-files", { fileIds: fileIds });

      if (res && !payload.error) {
        notificationManager.showNotification(
          res.success ? "success" : "error",
          res.success
            ? i18n.global.t("training.program.alert.file_uploaded_success", { count: payload.count })
            : i18n.global.t("training.program.alert.file_upload_error"),
        );
      }

      this.isUploadingFiles = false;
    },

    async remove(file) {
      const done = await trainingManager.remove(file.id, this.$route.params.trainingId, "program/file", "file");
      if (done && done.success) {
        this.$refs.dropzone.removeConfirmation(file);
      } else {
        this.$refs.dropzone.cancelRemove(file);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.workflow {
  background: $bg-grey;
  border: 1px solid $light-grey;
  border-radius: $radius;
  padding: 20px;
  display: flex;
  justify-content: space-between;

  @include up-to-lg {
    flex-direction: column;
  }

  @include dark-theme {
    background: $m_color_12;
    color: $white;
  }

  &__actions,
  &__dropzone {
    width: 50%;
    height: 100%;
    padding: 0 15px;

    @include up-to-lg {
      width: 100%;
      padding: 0;
    }
  }

  &__dropzone {
    border-right: 1px dashed $light-grey;

    @include up-to-lg {
      border-right: none;
      border-bottom: 1px dashed $light-grey;
      padding-bottom: 20px;
      margin-bottom: 20px;
    }
  }

  &__step {
    display: flex;
    align-items: center;

    &.-success {
      .workflow__step__number {
        background: $green;
      }

      .workflow__step__content__status {
        color: $green;
      }

      .workflow__step__content__label {
        color: var(--primary-color);

        @include dark-theme {
          color: $white;
        }
      }
    }

    &__number {
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: 700;
      background: $m_color_4;
      border-radius: 50%;
      width: 40px;
      min-width: 40px;
      height: 40px;
      margin-right: 8px;
      color: $white;
    }

    &__content {
      &__label {
        font-weight: 700;
        color: $m_color_4;
      }
      &__status,
      &__date {
        font-size: 12px;
        color: $m_color_4;
      }

      &__date {
        color: $green;
      }
    }
  }

  &__button {
    width: 100%;

    &.disabled {
      background: $m_color_4;
      border-color: $m_color_4;
    }
  }
}
</style>
